import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, Subject } from 'rxjs';
import { Area } from 'src/app/models/area.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { UserType } from 'src/app/models/customer.model';
import { KeyValueItem } from 'src/app/models/keyValueItem.model';
import { MvPoleDetails, MvPoleFilter, MvPoleStatsOptions, SortType } from 'src/app/models/mv-pole.model';
import { Region } from 'src/app/models/region.model';
import { SystemComponents, PermissionsEnum } from 'src/app/models/role.model';
import { UserForFilter } from 'src/app/models/user.model';
import { OptionObj } from 'src/app/models/util.model';
import { AreaService } from 'src/app/services/area.service';
import { MvPoleService } from 'src/app/services/mv-pole.service';
import { GeographicalRegion, GeographicalRegionService, PermissionsService, SubGeographicalRegion, SubGeographicalRegionService } from 'shared-lib';
import { RegionService } from 'src/app/services/region.service';
import { TranslationService } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-mv-poles',
  templateUrl: './mv-poles.component.html',
  styleUrls: ['./mv-poles.component.scss']
})
export class MvPolesComponent implements OnInit{
  stats: KeyValueItem[] = [];
  mvPoles: MvPoleDetails[] = [];
  filter: MvPoleFilter = new MvPoleFilter();
  allCheckboxesChecked: boolean = false;

  count: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;

  regions: Region[] = [];
  areas: Area[] = [];
  allAreas: Area[] = [];
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];
  hasCimDataIntegration: boolean = false;
  geographicalRegions: (GeographicalRegion & { checked?: boolean })[] = [];
  allSubGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  subGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  selectedSubGeographicalRegions: number[] = [];
  clicked: boolean = false;
  showUpload: boolean = false;
  utilityId: number = 0;
  
  showSortBy: boolean = false;
  sortByOptions = SortType;
  mapSortBy: Record<number, string> = {
    [SortType.NEWEST]: 'COMMON.SORT_BY.NEWEST',
    [SortType.SLRN_ASC]: 'COMMON.SORT_BY.SLRN_ASC',
    [SortType.SLRN_DESC]: 'COMMON.SORT_BY.SLRN_DESC',
    [SortType.ID_ASC]: 'COMMON.SORT_BY.ID_ASC',
    [SortType.ID_DESC]: 'COMMON.SORT_BY.ID_DESC',
  };
  sortByValue: SortType = SortType.NEWEST;
  sortByName: string = this.mapSortBy[SortType.NEWEST];

  selectedFilters: ChecklistItem[] = [];
  utName: string = this.translationService.getByKeyFromStorage('UT');

  poleType: number = 0;
  validationStatuses: number[] = [];
  userType?: number;
  allUsers: UserForFilter[] = [];
  selectedDateType: string;
  selectedDateTypeDisplay: string;
  showSelectDateType: boolean = false;
  searchFilterApplied: boolean = false;
  dateFrom: string;
  dateTo: string;
  searchText: string = '';
  searchTextUpdate = new Subject<string>();
  filteredUsers: UserForFilter[] = [];
  mobileUsers: UserForFilter[] = []; // Store only mobile users
  selectedUsers: number[] = [];
  adminUsers: UserForFilter[] = []; // Store only admin users

  poleStatusOptions = [
    { name: 'Pending', value: 0, checked: false, translation: 'COMMON.ASSET_STATUS.PENDING' },
    { name: 'Complete', value: 1, checked: false, translation: 'COMMON.ASSET_STATUS.COMPLETE' },
    { name: 'Assigned', value: 3, checked: false, translation: 'COMMON.ASSET_STATUS.ASSIGNED' },
    { name: 'Rejected', value: 4, checked: false, translation: 'COMMON.ASSET_STATUS.REJECTED' },
  ];

  poleTypesOptions: OptionObj[] = 
  [
    {
      "key": 1,
      "value": "COMMON.POLE.TYPE.CONCRETE"
    },
    {
      "key": 2,
      "value": "COMMON.POLE.TYPE.WOODEN"
    }
  ];

  userTypeOptions = [
    {
      name: 'First Captured User',
      value: UserType.FirstCapturedUser,
      translation: 'COMMON.USER.TYPE.FIRST_CAPTURED',
    },
    {
      name: 'Last Captured User',
      value: UserType.LastCapturedUser,
      translation: 'COMMON.USER.TYPE.LAST_CAPTURED',
    },
    {
      name: 'Reassigned By',
      value: UserType.ReassignedBy,
      translation: 'COMMON.USER.TYPE.REASSIGNED',
    },
    {
      name: 'Validated By',
      value: UserType.ValidatedBy,
      translation: 'COMMON.USER.TYPE.VALIDATED',
    },
    {
      name: 'Audited By',
      value: UserType.AuditedBy,
      translation: 'COMMON.USER.TYPE.AUDITED',
    },
  ];

  dateTypes = [
    {
      name: 'LastUpdateTime',
      value: 'Last Update Time',
      translation: 'COMMON.DATE_TYPE.LAST_UPDATE',
    },
    {
      name: 'TaggedDate',
      value: 'Tagged Date',
      translation: 'COMMON.DATE_TYPE.TAGGED',
    },
    {
      name: 'ValidatedDate',
      value: 'Validated Date',
      translation: 'COMMON.DATE_TYPE.VALIDATED',
    },
  ];

  constructor(
    private mvPoleService: MvPoleService,
    private toastr: ToastrService,
    private regionService: RegionService,
    private areaService: AreaService,
    private translationService: TranslationService,
    private userService: UserService,
    public permissionsService: PermissionsService,
    private authService: AuthService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService
  ) {
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
  }

  ngOnInit(): void {
    this.setFilters();
    if (this.hasCimDataIntegration) {
      this.getGeographicalRegions();
    } else {
      this.getRegions();
    }
    this.setCheckedUsers();
    this.onPageChange(this.currentPage);
    this.getStats();

    this.searchTextUpdate.pipe(debounceTime(500)).subscribe((_) => {
      if (this.searchText == '') this.search();
      this.searchFilterApplied = false;
    });
    this.selectedDateType = this.selectedDateType ?? this.dateTypes[0].name;
    this.selectedDateTypeDisplay =
      this.selectedDateTypeDisplay ?? this.dateTypes[0].translation;
  }

  async setCheckedUsers() {
    var filter = localStorage.getItem('filterMvPole');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'MvPole') return;
    if (
      filterObject.filter.filterParams.selectedUsers &&
      filterObject.filter.filterParams.userType
    ) {
      this.userType = filterObject.filter.filterParams.userType;
      this.getUsersForFilter(true, filterObject);
    }
  }

  onPageChange(currentPage: number) {
    this.currentPage = currentPage;
    this.reloadTable(currentPage);
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count
      },
     filterParams: this.getFilterObject(),
    };
    this.getAll(obj);
  }

  private getAll(obj: any) {
    this.mvPoleService.getAll(obj).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.mvPoles = responseData?.data?.data?.length
            ? responseData?.data?.data?.map((el: any) => new MvPoleDetails(el))
            : [];
          this.mvPoles.forEach((x) => {
            x.buildingsSLRN = x.buildingsSLRN?.replace(/,/g, ', ');
          });
          this.count = responseData.data.count ?? this.count;
          if (responseData.message !== '') {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  checkAllCheckboxes() {
    this.mvPoles.forEach((x) => (x.isChecked = !this.allCheckboxesChecked));
    this.allCheckboxesChecked = !this.allCheckboxesChecked;
  }

  checkPole(id: number) {
    const pole = this.mvPoles.find((x) => x.id === id);
    if (pole) {
      pole.isChecked = !pole.isChecked;
      this.areAllCheckboxesChecked();
    }
  }

  areAllCheckboxesChecked() {
    this.allCheckboxesChecked = this.mvPoles.every((x) => x.isChecked);
  }

  getStats() {
    this.getCardStats(MvPoleStatsOptions.Total);
    this.getCardStats(MvPoleStatsOptions.Kv11Feeders);
    this.getCardStats(MvPoleStatsOptions.Retagged);
    this.getCardStats(MvPoleStatsOptions.LvReticulation);
    this.getCardStats(MvPoleStatsOptions.GoodCondition);
  }

  private getCardStats(option: MvPoleStatsOptions) {
    var value = 0;
    var key = '';
    const filter = this.getFilterObject();
    const statsFilter = {
      filter: filter,
      option: option,
    };
    this.mvPoleService.getStats(statsFilter).subscribe({
      next: (response: any) => {
        switch (option) {
          case MvPoleStatsOptions.Total:
            value = response?.body?.data?.total ?? 0;
            key = MvPoleStatsOptions[MvPoleStatsOptions.Total];
            this.checkStats(key, value)
            break;
          case MvPoleStatsOptions.Kv11Feeders:
            value = response?.body?.data?.kV11Feeders ?? 0;
            key = MvPoleStatsOptions[MvPoleStatsOptions.Kv11Feeders];
            this.checkStats(key, value);            
            break;
          case MvPoleStatsOptions.Retagged:
            value = response?.body?.data?.rettagged ?? 0;
            key = MvPoleStatsOptions[MvPoleStatsOptions.Retagged];
            this.checkStats(key, value);            
            break;
          case MvPoleStatsOptions.LvReticulation:
            value = response?.body?.data?.lvReticulation ?? 0;
            key = MvPoleStatsOptions[MvPoleStatsOptions.LvReticulation];
            this.checkStats(key, value);            
            break;
          case MvPoleStatsOptions.GoodCondition:
            value = response?.body?.data?.goodCondition ?? 0;
            key = MvPoleStatsOptions[MvPoleStatsOptions.GoodCondition];
            this.checkStats(key, value);           
            break;
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }

  private checkStats(key: string, value: number){
    var stat = this.stats.find(
      (x) => x.key == key
    );
    if (stat) {
      stat.value = value;
    } else {
      this.addStat(key, value);
    }
  }

  private addStat(key: string, value: number) {
    const newItem = new KeyValueItem();
    newItem.key = key;
    newItem.value = value;

    var keyType: MvPoleStatsOptions =
      MvPoleStatsOptions[key as keyof typeof MvPoleStatsOptions];
    switch (keyType) {
      case MvPoleStatsOptions.Total:
        newItem.order = 1;
        newItem.friendlyName = 'MV_POLES.TOTAL';
        break;
      case MvPoleStatsOptions.Kv11Feeders:
        newItem.order = 2;
        newItem.friendlyName = 'MV_POLES.NUM_11KV_FEEDERS';
        break;
      case MvPoleStatsOptions.Retagged:
        newItem.order = 3;
        newItem.friendlyName = 'COMMON.ASSET_STATUS.RETAGGED'
        break;
      case MvPoleStatsOptions.LvReticulation:
        newItem.order = 4;
        newItem.friendlyName = 'MV_POLES.NUM_MV_LV_RETICULATION';
        break;
      case MvPoleStatsOptions.GoodCondition:
        newItem.order = 5;
        newItem.friendlyName = 'MV_POLES.IN_GOOD_COND';
        break;
    }

    this.stats.push(newItem);
    if (this.stats.length == 5)
      this.stats.sort((a, b) =>
        a.order < b.order ? -1 : a.order > a.order ? 1 : 0
      );
  }

  private getRegions() {
    this.regionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.setCheckedRegions();
          this.getAreas();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  setCheckedRegions() {
    var filter = localStorage.getItem('filterMvPole');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'MvPole') return;
    if (filterObject.filter.filterParams.selectedRegions) {
      this.selectedRegions = filterObject.filter.filterParams.selectedRegions;
      this.regions.forEach((element) => {
        if (this.selectedRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
    this.createFilterCards();
    this.removeFilterStorage();
  }

  removeFilterStorage() {
    if (this.allAreas.length > 0 && this.regions.length > 0)
      localStorage.removeItem('filterMvPole');
  }
  
  private getAreas() {
    this.areaService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.areas = this.allAreas.filter((area) =>
            this.regions.some(
              (region) => region.id == area.regionId && region.checked
            )
          );
          this.setCheckedAreas();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  setCheckedAreas() {
    var filter = localStorage.getItem('filterMvPole');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'MvPole') return;
    if (filterObject.filter.filterParams.selectedAreas) {
      this.selectedAreas = filterObject.filter.filterParams.selectedAreas;
      this.areas.forEach((element) => {
        if (this.selectedAreas.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
    this.createFilterCards();
    this.removeFilterStorage();
  }

   private getFilterObject() {
    let obj = {
      sortBy: this.sortByValue,
      type: this.poleType,
      selectedAreas: this.selectedAreas,
      selectedRegions: this.selectedRegions,
      search: this.searchText,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      userType: this.userType
        ? this.selectedUsers.length > 0
          ? this.userType
          : null
        : null,
      selectedUsers: this.selectedUsers.length > 0 ? this.selectedUsers : null,
      dateType: this.selectedDateType,
      validationStatuses: this.validationStatuses,
      selectedGeographicalRegions:
        this.geographicalRegions.filter((x) => x.checked).map((x) => x.id).length > 0
          ? this.geographicalRegions.filter((x) => x.checked).map((x) => x.id)
          : null,
      selectedSubGeographicalRegions:
        this.selectedSubGeographicalRegions.length > 0
          ? this.selectedSubGeographicalRegions
          : null,
    };
    return obj;
  }

  applyFilter() {
    this.reloadTable(1);
    this.getStats();
  }

  resetFilter(e: any) {
    this.selectedFilters = [];

    this.regions.forEach((region) => {
      region.checked = false;
    });
    this.areas.forEach((area) => {
      area.checked = false;
    });
    this.selectedRegions = [];
    this.areas = [];
    this.selectedAreas = [];
    this.geographicalRegions.forEach((region) => {
      region.checked = false;
    });
    this.subGeographicalRegions.forEach((sub) => {
      sub.checked = false;
    });
    this.subGeographicalRegions = [];
    this.selectedSubGeographicalRegions = [];
    this.poleType = 0;
    this.searchText = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = this.filteredUsers = [...[]];
    this.userType = undefined;
    this.poleStatusOptions.forEach((status) => {
      status.checked = false;
    });
    this.validationStatuses = [];
    this.clicked = !this.clicked;

    this.reloadTable(1);
    this.getStats();
  }

  removeFilter(item: ChecklistItem) {
    if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) {
          area.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Pole Type') {
      this.poleType = 0;
    } else if (item.property == 'Search') {
      this.searchText = '';
    } else if (item.property == 'Date From') {
      this.dateFrom = '';
    } else if (item.property == 'Date To') {
      this.dateTo = '';
    } else if (item.property == 'User') {
      this.allUsers.forEach((user) => {
        if (user.name === item.selectedValue) {
          user.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'User type') {
      this.userType = undefined;
      this.allUsers.forEach((user) => {
        // Clear list if he selects same filter again to get all cleared
        user.checked = false;
      });
      this.allUsers = this.filteredUsers = [...[]];
    } else if (item.property == 'Geographical Region') {
      this.geographicalRegions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Areas') {
      this.subGeographicalRegions.forEach((sub) => {
        if (sub.name === item.selectedValue) {
          sub.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Pole Status') {
      this.poleStatusOptions.forEach((status) => {
        if (status.name === item.selectedValue) {
          status.checked = false;
        }
      });
      this.clicked = !this.clicked;
    }

    this.createFilterCards();

    if (this.selectedFilters.length == 0) {
      this.regions.forEach((region) => {
        region.checked = false;
      });
      this.reloadTable(1);
      this.getStats();
    }
  }

  selectPoleStatus(e: any) {
    this.createFilterCards();
  }

  resetUserList() {
    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = [];
  }

  selectUserType(e: any) {
    if (this.userType) this.resetUserList();

    if (e.target.value != '') {
      this.userType = e.target.value;
      this.getUsersForFilter(false);
    } else {
      this.userType = undefined;
    }

    this.createFilterCards();
  }

  getUsersForFilter(isFromCache: boolean, filterObject?: any) {
      if (
        this.userType == UserType.FirstCapturedUser ||
        this.userType == UserType.LastCapturedUser
      ) {
        // Dobavi mobilne usere iz baze ako nisi
        if (this.mobileUsers.length === 0) {
          this.userService
            .getUsersForFilter(SystemComponents.Mobile)
            .subscribe((resp) => {
              this.mobileUsers = resp.data;
              this.allUsers = this.filteredUsers = this.mobileUsers;
              if (isFromCache) {
                this.selectedUsers =
                  filterObject.filter.filterParams.selectedUsers;
                this.allUsers.forEach((element) => {
                  if (this.selectedUsers.includes(element.id))
                    element.checked = true;
                  else element.checked = false;
                });
              }
              this.createFilterCards();
            });
        } else {
          this.allUsers = this.filteredUsers = [...this.mobileUsers];
        }
      } else {
        let permission = PermissionsEnum.MVPole_Assign;
  
        switch (Number(this.userType)) {
          case UserType.ValidatedBy:
            permission = PermissionsEnum.MVPole_ApproveReject;
            break;
          case UserType.AuditedBy:
            permission = PermissionsEnum.MVPole_AuditReview;
            break;
        }
        if (this.adminUsers.length === 0) {
          this.userService
            .getUsersForFilter(SystemComponents.Admin)
            .subscribe((resp) => {
              this.adminUsers = resp.data;
              this.allUsers = this.filteredUsers = [
                ...this.adminUsers.filter((x) =>
                  x.permissions?.some((x) => Number(x) == permission)
                ),
              ];
              if (isFromCache) {
                this.selectedUsers =
                  filterObject.filter.filterParams.selectedUsers;
                this.allUsers.forEach((element) => {
                  if (this.selectedUsers.includes(element.id))
                    element.checked = true;
                  else element.checked = false;
                });
                this.createFilterCards();
              }
            });
        } else {
          this.allUsers = this.filteredUsers = [
            ...this.adminUsers.filter((x) =>
              x.permissions?.some((x) => Number(x) == permission)
            ),
          ];
        }
      }
    }

  selectDateType(dateType: any) {
    this.selectedDateType = dateType.name;
    this.selectedDateTypeDisplay = dateType.translation;
    this.showSelectDateType = false;
  }

  datesValid() {
    if (this.dateFrom && this.dateTo && this.dateTo?.toString() != '') {
      const valid: boolean =
        new Date(this.dateFrom).getTime() <
        new Date(`${this.dateTo.toString()}T23:59:59`).getTime();
      return valid;
    }
    return true;
  }

  applySearchFilter(e: any) {
    this.filteredUsers = this.allUsers.filter((user) => user.name.toLowerCase().includes(e.toLowerCase()));
  }

  selectUsers(e: any) {
    if (e.status) {
      this.allUsers.push(e.value);
    } else {
      var index = this.allUsers.indexOf(e.value);
      this.allUsers.splice(index, 1);
    }
    this.createFilterCards();
  }

  setFilters() {
    var filter = localStorage.getItem('filterMvPole');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'MvPole') return;

    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;

    this.searchText = filterObject.filter.filterParams.search;
    this.selectedDateType = filterObject.filter.filterParams.dateType;
    this.selectedDateTypeDisplay =
      this.dateTypes.find(
        (type) => type.name == filterObject.filter.filterParams.dateType
      )?.translation ?? this.dateTypes[0].translation;
    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;
    this.sortByValue = filterObject.filter.filterParams.sortBy;
    this.sortByName = this.mapSortBy[filterObject.filter.filterParams.sortBy];
   
    this.selectedRegions = filterObject.filter.filterParams.selectedRegions
      ? filterObject.filter.filterParams.selectedRegions
      : [];
    this.selectedAreas = filterObject.filter.filterParams.selectedAreas
      ? filterObject.filter.filterParams.selectedAreas
      : [];
    this.selectedSubGeographicalRegions = filterObject.filter.filterParams.selectedSubGeographicalRegions
      ? filterObject.filter.filterParams.selectedSubGeographicalRegions
      : [];

    this.userType = filterObject.filter.filterParams.userType;
    this.selectedUsers = filterObject.filter.filterParams.selectedUsers
      ? filterObject.filter.filterParams.selectedUsers
      : [];

    
    this.poleType = filterObject.filter.filterParams.type;
    this.validationStatuses = filterObject.filter.filterParams.validationStatuses
      ? filterObject.filter.filterParams.validationStatuses
      : [];
    this.poleStatusOptions.forEach((status) => {
      if(this.validationStatuses.includes(status.value))status.checked = true;  
    });
  }

  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };

    // Remove 'T23:59:59' from the dateTo to avoid errors
    if (obj.filterParams.dateTo) {
      obj.filterParams.dateTo = obj.filterParams.dateTo.replace(
        'T23:59:59',
        ''
      );
    }

    localStorage.setItem(
      'filterMvPole',
      JSON.stringify({ type: 'MvPole', filter: obj })
    );
  }

  createFilterCards() {
    this.selectedFilters = [];

    this.selectedRegions = this.regions
      .filter((x) => x.checked)
      .map((x) => x.id);
    let selectedAreas = this.areas.filter((x) => x.checked);
    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);
    let selectedStatuses = this.poleStatusOptions.filter((x) => x.checked);
    this.validationStatuses = this.poleStatusOptions.filter((x) => x.checked).map((x) => x.value);
    let selectedUsers = this.allUsers.filter((x) => x.checked);
    this.selectedUsers = this.allUsers
      .filter((x) => x.checked)
      .map((x) => x.id);
    let selectedSubGeoRegions = this.subGeographicalRegions.filter((x) => x.checked);
    this.selectedSubGeographicalRegions = this.subGeographicalRegions.filter((x) => x.checked).map((x) => x.id as number);

    //areas
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          this.utName,
          selectedAreas[i].name,
          'COMMON.UT.SINGLE'
        )
      );
    }

    //sub geographical regions
    for (var i = 0; i < selectedSubGeoRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Areas',
          selectedSubGeoRegions[i].name,
          'COMMON.UT.SINGLE'
        )
      );
    }

    if (this.poleType != 0 && this.poleTypesOptions.length > 0) {
      let poleName = this.poleTypesOptions.filter(
        (x) => x.key === this.poleType
      )[0].value;
      this.selectedFilters.push(
        new ChecklistItem(
          'Pole Type',
          poleName,
          'COMMON.TABLE_HEADERS.POLE_TYPE',
        )
      );
    }

    selectedStatuses.forEach(status => {
      this.selectedFilters.push(
        new ChecklistItem(
          'Pole Status',
          status.name,
          'COMMON.POLE.STATUS',
          status.translation
        )
      );
    })

    if (this.searchText) {
      this.selectedFilters.push(
        new ChecklistItem('Search', this.searchText, 'COMMON.SEARCH')
      );
    }

    if (this.dateFrom) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Date From',
          this.dateFrom.toString(),
          'COMMON.DATE_FROM'
        )
      );
    }

    if (this.dateTo) {
      this.selectedFilters.push(
        new ChecklistItem('Date To', this.dateTo.toString(), 'COMMON.DATE_TO')
      );
    }

    if (this.userType) {
      var userTypeName = UserType[this.userType!];
      var userTypeTranslationKey = this.userTypeOptions.find(
        (option) => option.value == this.userType
      )?.translation;
      this.selectedFilters.push(
        new ChecklistItem(
          'User type',
          userTypeName,
          'COMMON.USER.TYPE.TITLE',
          userTypeTranslationKey
        )
      );
    }
  
    //users
    for (var i = 0; i < selectedUsers.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('User', selectedUsers[i].name, 'COMMON.USER.SINGLE')
      );
    }
  }

  search() {
    this.searchFilterApplied = this.searchText ? true : false;
    this.createFilterCards();
  }

  sortByMethod(option: SortType) {
    this.sortByValue = option;
    this.sortByName = this.mapSortBy[option];
    this.reloadTable();
  }

  download() {
    const obj = this.getFilterObject();
    this.mvPoleService.download(obj).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else {
          this.toastr.error(data?.message || 'Request failed');
        }
      },
      error: (error) => this.toastr.error(error),
    });
  }

  selectRegion(e: any) {
    //list of areas for selected regions
    this.areas = this.allAreas.filter((area) =>
      this.regions.some(
        (region) => region.id == area.regionId && region.checked
      )
    );
    this.allAreas.forEach((area) => {
      if (!this.areas.some((a) => a.id === area.id)) {
        area.checked = false; //uncheck areas for unchecked regions
      }
    });

    this.createFilterCards();
  }

  selectArea(e: any) {
    this.createFilterCards();
  }

  selectPoleType(e: any) {
    this.poleType = +e.target.value;
    this.createFilterCards();
  }

  private getGeographicalRegions() {
    this.geographicalRegionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geographicalRegions = responseData.data;
          this.setCheckedGeographicalRegions();
          this.getSubGeographicalRegions();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getSubGeographicalRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSubGeographicalRegions = responseData.data;
          this.subGeographicalRegions = this.allSubGeographicalRegions.filter(
            (sub) =>
              this.geographicalRegions.some(
                (region) => region.id == sub.geographicalRegion.id && region.checked
              )
          );
          this.setCheckedSubGeographicalRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  setCheckedGeographicalRegions() {
    var filter = localStorage.getItem('filterMvPole');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'MvPole') return;
    if (filterObject.filter.filterParams.selectedGeographicalRegions) {
      const selectedIds = filterObject.filter.filterParams.selectedGeographicalRegions;
      this.geographicalRegions.forEach((element) => {
        if (selectedIds.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
    this.createFilterCards();
    this.removeFilterStorage();
  }

  setCheckedSubGeographicalRegions() {
    var filter = localStorage.getItem('filterMvPole');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'MvPole') return;
    if (filterObject.filter.filterParams.selectedSubGeographicalRegions) {
      this.selectedSubGeographicalRegions = filterObject.filter.filterParams.selectedSubGeographicalRegions;
      this.subGeographicalRegions.forEach((element) => {
        if (this.selectedSubGeographicalRegions.includes(element.id as number)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  selectGeographicalRegion(e: any) {
    this.subGeographicalRegions = this.allSubGeographicalRegions.filter((sub) =>
      this.geographicalRegions.some(
        (region) => region.id == sub.geographicalRegion.id && region.checked
      )
    );
    this.allSubGeographicalRegions.forEach((sub) => {
      if (!this.subGeographicalRegions.some((s) => s.id === sub.id)) {
        sub.checked = false;
      }
    });
    this.createFilterCards();
  }

  ngOnDestroy(): void {
    this.searchTextUpdate.complete();
  }
}